// @owners { team: patient-health }
// eslint-disable-next-line @alto/no-pocky-import
import { Text } from '@alto/pocky';
import autobind from 'autobind-decorator';
import React, { type ReactNode } from 'react';
import { type Medication } from '~shared/features/my-meds/types';
import { getDisplayName } from '~shared/helpers/helper';
import { type User } from '~shared/types/clients';
import CollapsibleSection from './CollapsibleSection';
import { MedListContainer } from './MedListActive';
import { MedListRowContainer } from './MedListRow';
import MedListRow from './MedListRowContainer';

type Props = {
  readonly familyMembers: User[];
  readonly hasFamily: boolean;
  readonly inactive: Medication[];
  readonly initialRxNumber: string | null | undefined;
};

export default class MedListInactive extends React.PureComponent<Props> {
  static defaultProps = {
    initialRxNumber: null,
  };

  componentDidMount() {
    // ref is only set when rx number matches
    // @ts-expect-error TS(2339): Property 'previousSibling' does not exist on type 'never'.
    if (this.rowRef?.previousSibling) {
      // just using offsetTop scrolls you to a position where the med at the
      // top is the previous medication in the list. this will scroll down a bit
      // to be clear that we're scrolling the user to the row below.
      // @ts-expect-error TS(2339): Property 'offsetTop' does not exist on type 'never'.
      const scrollPosition = this.rowRef.offsetTop + this.rowRef.previousSibling.clientHeight / 2;
      window.scrollTo(0, scrollPosition);
    }
  }

  rowRef = null;

  // @ts-expect-error error TS1270: Decorator function return type 'void | TypedPropertyDescriptor<unknown>' is not assignable to type 'void | (() => void)'
  @autobind
  setMedListRowRef(element: HTMLDivElement | null | undefined) {
    // @ts-expect-error TS(2322): Type 'HTMLDivElement | null | undefined' is not assignable to type 'null'.
    this.rowRef = element;
  }

  // @ts-expect-error error TS1270: Decorator function return type 'void | TypedPropertyDescriptor<unknown>' is not assignable to type 'void | (() => void)'
  @autobind
  renderMedications(medications: Medication[], showUserTag = false): ReactNode[] {
    const { initialRxNumber } = this.props;

    return medications.map((medication) => {
      const match = medication?.prescriptions?.some?.((p) => p.rx_number === initialRxNumber) ?? false;

      return (
        <div
          // eslint-disable-next-line @typescript-eslint/unbound-method
          ref={match ? this.setMedListRowRef : null}
          key={medication.prescriptions[0].rx_number}
        >
          <MedListRow
            medication={medication}
            initShowDetail={match}
            showUserTag={showUserTag}
          />
        </div>
      );
    });
  }

  // @ts-expect-error error TS1270: Decorator function return type 'void | TypedPropertyDescriptor<unknown>' is not assignable to type 'void | (() => void)'
  @autobind
  renderInactiveMedications(medications: Medication[], user: User | null | undefined = null) {
    const greeting = user ? `${getDisplayName(user)} doesn't` : "You don't";

    return medications.length > 0 ? (
      this.renderMedications(medications)
    ) : (
      <MedListRowContainer short>
        <Text>
          {greeting} have any inactive medications. Any archived, discontinued, or transferred medications will show up
          here.
        </Text>
      </MedListRowContainer>
    );
  }

  render() {
    const { familyMembers, hasFamily, inactive } = this.props;

    return (
      <>
        {hasFamily ? (
          familyMembers.map((user) => {
            const title = `${getDisplayName(user)}'s Meds`;
            const medications = inactive.filter((medication) => user.id === medication.userID);

            return (
              <CollapsibleSection
                key={user.id}
                title={title}
                darkHeader
              >
                {this.renderInactiveMedications(medications, user)}
              </CollapsibleSection>
            );
          })
        ) : (
          <MedListContainer>{this.renderInactiveMedications(inactive)}</MedListContainer>
        )}
      </>
    );
  }
}
